import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import * as typeOfInstallationApi from '../../api/materialAccessoryApi';
import { bust } from '../../utils/apiCache';
import { useSnackbar } from '../../context/SnackbarContext';
import { useAuth } from '../../context/AuthContext';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const EMPTY_FORM = { name: '', description: '', isActive: true };

export default function TypeOfInstallationTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { showSnackbar } = useSnackbar();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('MANAGE_MASTER_DATA') || hasPermission('MANAGE_TYPE_OF_INSTALLATION');
  const canDelete = hasPermission('DELETE_MASTER_DATA') || hasPermission('DELETE_TYPE_OF_INSTALLATION') || hasPermission('MANAGE_MASTER_DATA');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await typeOfInstallationApi.getTypesOfInstallation();
      setItems(res.data || res);
    } catch (e) { showSnackbar(e.message, 'error'); }
    finally { setLoading(false); }
  }, [showSnackbar]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditItem(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (item) => { setEditItem(item); setForm({ name: item.name, description: item.description || '', isActive: item.isActive }); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.name?.trim()) { showSnackbar('Name is required', 'error'); return; }
    setSaving(true);
    try {
      if (editItem) {
        await typeOfInstallationApi.updateTypeOfInstallation(editItem.id, form);
        showSnackbar('Type of installation updated', 'success');
      } else {
        await typeOfInstallationApi.createTypeOfInstallation(form);
        showSnackbar('Type of installation created', 'success');
      }
      setDialogOpen(false);
      bust('typesOfInstallation');
      load();
    } catch (e) {
      showSnackbar(e.response?.data?.message || e.message, 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await typeOfInstallationApi.deleteTypeOfInstallation(deleteTarget.id);
      showSnackbar('Type of installation deleted', 'success');
      setDeleteTarget(null);
      bust('typesOfInstallation');
      load();
    } catch (e) {
      showSnackbar(e.response?.data?.message || e.message, 'error');
      setDeleteTarget(null);
    }
  };

  if (!canManage && !canDelete) {
    return <Alert severity="info">You have read-only access to type of installation options.</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        {canManage && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            size="small"
            sx={{ backgroundColor: '#6A1B9A', '&:hover': { backgroundColor: '#6A1B9A' } }}
          >
            Add Installation Type
          </Button>
        )}
      </Box>

      {loading ? <LoadingSpinner /> : (
        <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Used By</TableCell>
              {(canManage || canDelete) && <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, i) => (
              <TableRow key={item.id} hover>
                <TableCell>{i + 1}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{item.name}</TableCell>
                <TableCell sx={{ color: '#757575', fontSize: '0.9rem' }}>{item.description || '—'}</TableCell>
                <TableCell>
                  <Chip
                    label={item.isActive ? 'Active' : 'Inactive'}
                    size="small"
                    color={item.isActive ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell sx={{ fontSize: '0.9rem' }}>
                  {item._count?.materialAccessories || 0} link(s)
                </TableCell>
                {(canManage || canDelete) && (
                  <TableCell align="right">
                    {canManage && (
                      <IconButton
                        size="small"
                        onClick={() => openEdit(item)}
                        title="Edit"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    {canDelete && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteTarget(item)}
                        title="Delete"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
            {!items.length && (
              <TableRow>
                <TableCell
                  colSpan={canManage || canDelete ? 6 : 5}
                  align="center"
                  sx={{ py: 4, color: '#9e9e9e' }}
                >
                  No installation types defined. Create one to get started.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#6A1B9A', fontWeight: 600 }}>
          {editItem ? 'Edit Type of Installation' : 'Add Type of Installation'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField
            autoFocus
            label="Name"
            fullWidth
            placeholder="e.g., Surface Installation, Underground, Wall-mounted"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            error={!form.name?.trim() && dialogOpen}
            helperText={!form.name?.trim() && dialogOpen ? 'Name is required' : ''}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            placeholder="Optional description to help users understand when to use this type..."
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          {editItem && (
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!form.name?.trim() || saving}
            sx={{ backgroundColor: '#6A1B9A', '&:hover': { backgroundColor: '#6A1B9A' } }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Type of Installation"
        message={`Delete "${deleteTarget?.name}"? ${
          deleteTarget?._count?.materialAccessories
            ? `This will cascade-delete ${deleteTarget._count.materialAccessories} material-accessory link(s).`
            : ''
        }`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
